import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Chip,
  Skeleton,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import BackButton from '../components/BackButton';

// Componente de tarjeta para vista móvil
const PeriodoCard = ({ periodo, onEdit, onDelete }) => {
  return (
    <Card sx={{
      mb: 2,
      borderRadius: 3,
      border: '1px solid rgba(26, 35, 126, 0.1)',
      borderLeft: `4px solid ${periodo.is_active ? '#4CAF50' : '#9E9E9E'}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
            {periodo.mes}/{periodo.anio}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {periodo.contable && (
              <Chip
                label="Contable"
                size="small"
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            <Chip
              icon={periodo.is_active ? <CheckCircleIcon /> : <CancelIcon />}
              label={periodo.is_active ? 'Activo' : 'Inactivo'}
              size="small"
              sx={{
                bgcolor: periodo.is_active ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                color: periodo.is_active ? '#4CAF50' : '#9E9E9E',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Tooltip title="Editar periodo">
            <IconButton
              size="small"
              onClick={() => onEdit(periodo)}
              sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar periodo">
            <IconButton
              size="small"
              onClick={() => onDelete(periodo.id_periodo)}
              sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

function PeriodosAdmin() {
  const [periodos, setPeriodos] = useState([]);
  const [filteredPeriodos, setFilteredPeriodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('anio');
  const [order, setOrder] = useState('desc');
  const [error, setError] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchPeriodos();
  }, []);

  useEffect(() => {
    const filterAndSortPeriodos = () => {
      let filtered = [...periodos];

      if (searchTerm) {
        filtered = filtered.filter(periodo =>
          Object.values(periodo)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredPeriodos(filtered);
      setPage(0);
    };

    filterAndSortPeriodos();
  }, [periodos, searchTerm, order, orderBy]);

  const fetchPeriodos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/periodos`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setPeriodos(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los periodos:', error);
      setError('Error al cargar los periodos. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de desactivar este periodo?')) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/periodos/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchPeriodos();
      } catch (error) {
        console.error('Error al desactivar el periodo:', error);
        setError('Error al eliminar el periodo.');
      }
    }
  };

  const handleEdit = (periodo) => {
    navigate(`/home/periodos/editar/${periodo.id_periodo}`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BackButton />
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              📅 Gestión de Periodos Contables
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administra los periodos contables de tu empresa
            </Typography>
          </Box>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/periodos/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Periodo
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por año, mes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredPeriodos
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((periodo) => (
              <PeriodoCard
                key={periodo.id_periodo}
                periodo={periodo}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}

          {filteredPeriodos.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredPeriodos.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)'
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'anio'}
                      direction={orderBy === 'anio' ? order : 'asc'}
                      onClick={createSortHandler('anio')}
                      sx={{
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Año
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'mes'}
                      direction={orderBy === 'mes' ? order : 'asc'}
                      onClick={createSortHandler('mes')}
                      sx={{
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Mes
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Contable
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPeriodos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((periodo) => (
                    <TableRow
                      key={periodo.id_periodo}
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(26, 35, 126, 0.02)'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(79, 195, 247, 0.1)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s ease'
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                          {periodo.anio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {periodo.mes}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {periodo.contable ? (
                          <Chip
                            label="Sí"
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        ) : (
                          <Chip
                            label="No"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={periodo.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                          label={periodo.is_active ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            bgcolor: periodo.is_active ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                            color: periodo.is_active ? '#4CAF50' : '#9E9E9E',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar periodo">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(periodo)}
                              sx={{
                                bgcolor: 'primary.light',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.main' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar periodo">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(periodo.id_periodo)}
                              sx={{
                                bgcolor: 'error.light',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.main' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredPeriodos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/periodos/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}

export default PeriodosAdmin;
